import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../utils/translator.helper';
import { ItemsID } from '../types';

@customElement('gz-help-and-support-tabs')
export class GzHelpAndSupportTabs extends LitElement {
  @property({ type: Object, attribute: true, reflect: false })
  cards = {};

  @property({ type: Object, attribute: true, reflect: false })
  features = {};

  @property({ type: Object, attribute: true, reflect: false })
  rights = {};
  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="d-flex flex-column">
        <gz-tab-group class="help-and-support-tab-group">
          <gz-tab label="${translateFn('Basics')}">
            <div class="help-and-support-tab-content grid-layout">${this.renderBasicsCards()}</div>
          </gz-tab>
          <gz-tab label="${translateFn('Advanced configuration')}">
            <div class="help-and-support-tab-content grid-layout">${this.renderAdvancedConfigurationCards()}</div>
          </gz-tab>
        </gz-tab-group>
      </div>
    `;
  }

  private renderBasicsCards(): TemplateResult {
    const popularCategories = Object.values(this.cards['basics']);

    return html`
      ${popularCategories.map((popularCategory: []) => {
        return html`
          <gz-card name="${popularCategory['title']}" icon="${popularCategory['icon']}">
            <div class="articles-wrapper d-flex">${this.renderArticles(popularCategory['articles'])}</div>
            <gz-link href="${popularCategory['btn']['link']}" class="more-articles-link">
              <span>${popularCategory['btn']['text']}</span>
            </gz-link>
          </gz-card>
        `;
      })}
    `;
  }

  private renderAdvancedConfigurationCards(): TemplateResult {
    let items: Record<string, any>[] = Object.values(this.cards['advancedConfiguration']);

    if (this.rights && this.features) {
      items = this.checkRenderEmailSecurity(items);
      items = this.checkRenderAntiMalware(items);
      items = this.filterItems(items);
    }

    return html`
      ${items.map((item: []) => {
        return html`
          <gz-card name="${item['title']}" icon="${item['icon']}">
            <div class="articles-wrapper d-flex">${this.renderArticles(item['articles'])}</div>
            <gz-link href="${item['btn']['link']}" class="more-articles-link">
              <span>${item['btn']['text']}</span>
            </gz-link>
          </gz-card>
        `;
      })}
    `;
  }

  /**
   * This function check if email security section/card should be rendered based on user license/feature and user rights
   */
  private checkRenderEmailSecurity(items: Record<string, any>[]): Record<string, any>[] {
    return items.filter(item => {
      const isEmailSecurityItem = item['id'] === ItemsID.EMAIL_SECURITY;
      const isExcluded =
        isEmailSecurityItem && (!this.rights['manageInventory'] || !this.features['emailSecurityVisible']);
      return !isExcluded;
    });
  }

  /**
   * This function check if anti-malware section/card should be rendered based on user license/feature and user rights
   */
  private checkRenderAntiMalware(items: Record<string, any>[]): Record<string, any>[] {
    return items.filter(item => {
      const isAntiMalwareItem = item['id'] === ItemsID.ANTI_MALWARE;
      const isExcluded = isAntiMalwareItem && this.rights['manageInventory'] && this.features['emailSecurityVisible'];
      return !isExcluded;
    });
  }

  private renderArticles(articles: Record<string, any>[]): TemplateResult {
    let articlesArray: Record<string, any>[] = Object.values(articles);

    if (this.rights && this.features) {
      articlesArray = this.filterItems(articlesArray);
    }

    return html`
      ${articlesArray.map((article: []) => {
        return html`
          <gz-link href="${article['link']}" class="article fs-14">
            <span>${article['text']}</span>
          </gz-link>
        `;
      })}
    `;
  }

  /**
   * This function filter items with a particular ID from items array that should be excluded
   * based on feature, because some elements should or should not be visible
   * based on the feature/license that the user have it set
   */
  private filterItems(items: Record<string, any>[]): Record<string, any>[] {
    const itemsToBeRemovedWhenFeatureMissing: { id: string; feature: boolean }[] = [
      { id: ItemsID.PATCH_MANAGEMENT, feature: this.features['patchManagementVisible'] },
      { id: ItemsID.APPLICATION_CONTROL, feature: this.features['applicationControl'] },
      { id: ItemsID.SECURITY_SERVER, feature: this.features['remoteEnginesScanningVisible'] },
      { id: ItemsID.MOBILE_SECURITY, feature: this.features['mobileSecurityVisible'] },
      { id: ItemsID.CLOUD_SECURITY, feature: this.features['cloudWorkloadSecurityUsable'] },
      { id: ItemsID.SECURITY_FOR_EXCHANGE, feature: this.features['exchangeVisible'] },
      { id: ItemsID.NETWORK_ATTACK_DEFENSE, feature: this.features['networkMonitorVisible'] },
      { id: ItemsID.CONTENT_CONTROL, feature: this.features['contentControlVisible'] },
      { id: ItemsID.USING_TAGS, feature: this.features['tagsVisible'] },
      { id: ItemsID.KASEYA, feature: Application.Main.company.hasMonthlyLicense() },
      { id: ItemsID.CONNECT_WISE, feature: Application.Main.company.hasMonthlyLicense() },
    ];

    const itemsToBeRemovedWhenFeatureExists: { id: string; feature: boolean }[] = [
      { id: ItemsID.AMAZON, feature: this.features['cop'] },
    ];

    let filteredItems: Record<string, any>[] = items;

    itemsToBeRemovedWhenFeatureMissing.forEach(item => {
      filteredItems = this.excludeItemsIfFeatureMissing(item.id, item.feature, filteredItems);
    });

    itemsToBeRemovedWhenFeatureExists.forEach(item => {
      filteredItems = this.excludeItemsIfFeatureExists(item.id, item.feature, filteredItems);
    });

    return filteredItems;
  }

  /**
   * This function exclude items from items array based on elementID and if the feature have FALSE status
   */
  private excludeItemsIfFeatureMissing(
    elementId: string,
    featureStatus: boolean,
    items: Record<string, any>[]
  ): Record<string, any>[] {
    return items.filter(item => {
      const isElement = item['id'] === elementId;
      const isExcluded = isElement && !featureStatus;
      return !isExcluded;
    });
  }

  /**
   * This function exclude items from items array based on elementID and if the feature have TRUE status
   */
  private excludeItemsIfFeatureExists(
    elementId: string,
    featureStatus: boolean,
    items: Record<string, any>[]
  ): Record<string, any>[] {
    return items.filter(item => {
      const isElement = item['id'] === elementId;
      const isExcluded = isElement && featureStatus;
      return !isExcluded;
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-help-and-support-tabs': GzHelpAndSupportTabs;
  }
}
